import { useInfiniteQuery, useMutation } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";

import { useGraphQLClient } from "../graphql/client";
import { searchMetaobjectQuery } from "../graphql/metaobject-gql";
import { INFINITE_LIST_QUERY_OPTIONS, useCacheFirstRequest } from "../graphql/queryClient";
import { predictiveSearchQuery, searchQuery } from "../graphql/search-gql";
import type { ISearchMetaObjectsQuery } from "../interface/metaobject";
import type { IPredictiveSearchQuery, ISearchQuery } from "../interface/search";
import { locale } from "../locales";
import { ToastType } from "../types/layout";
import type { IUseSearchQuery } from "../types/queries";

function mergeSearchPages(pages: ISearchQuery[] | undefined): ISearchQuery | undefined {
  if (!pages?.length) return undefined;

  // search가 없으면(잘못된 검색 등) 그대로 반환 → 호출부의 `!data.search` 분기 유지
  const firstSearch = pages[0]?.search;
  if (!firstSearch) return { search: firstSearch } as ISearchQuery;

  const lastSearch = pages[pages.length - 1].search;

  return {
    search: {
      ...lastSearch,
      edges: pages.flatMap((page) => page.search?.edges ?? []),
      pageInfo: lastSearch.pageInfo,
      productFilters: firstSearch.productFilters,
      totalCount: firstSearch.totalCount,
    },
  } as ISearchQuery;
}

export default function useSearchQuery({ lang, openToast, initialSearchVariables = null }: IUseSearchQuery) {
  const request = useGraphQLClient();
  const cacheFirstRequest = useCacheFirstRequest();

  const [searchVariables, setSearchVariables] = useState<Record<string, unknown> | null>(initialSearchVariables);

  // 추천 검색어. 아카이브 세일처럼 시즌별로 다른 메타오브젝트를 쓰는 스토어는 type 을 넘긴다 (tune)
  const suggestMutation = useMutation({
    // void 를 포함해야 인자 없이 fetchSuggest() 로 부르는 기존 호출부가 그대로 컴파일된다
    mutationFn: (variables: { type?: string } | void) =>
      cacheFirstRequest<ISearchMetaObjectsQuery>("search-suggest", searchMetaobjectQuery, {
        type: variables?.type ?? "search_suggestions",
        first: 10,
        reverse: true,
      }),
    onError: (error) => {
      openToast({ message: locale(lang).error.server, type: ToastType.Error });
      throw new Error(error.message);
    },
  });

  const predictiveMutation = useMutation({
    mutationFn: (variables: Record<string, unknown>) =>
      cacheFirstRequest<IPredictiveSearchQuery>("search-predictive", predictiveSearchQuery, variables),
    onError: (error) => {
      openToast({ message: locale(lang).error.server, type: ToastType.Error });
      throw new Error(error.message);
    },
  });

  const searchInfinite = useInfiniteQuery({
    ...INFINITE_LIST_QUERY_OPTIONS,
    queryKey: ["shopifyV2", "search", searchVariables],
    enabled: searchVariables !== null,
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => request<ISearchQuery>(searchQuery, { ...searchVariables, after: pageParam }),
    getNextPageParam: (lastPage) =>
      lastPage.search?.pageInfo?.hasNextPage ? (lastPage.search.pageInfo.endCursor ?? undefined) : undefined,
  });

  // useQuery 계열은 v5에서 onError 콜백이 없으므로 에러 토스트는 effect로 처리
  useEffect(() => {
    if (searchInfinite.error) openToast({ message: locale(lang).error.server, type: ToastType.Error });
  }, [searchInfinite.error]);

  const searchData = useMemo(() => mergeSearchPages(searchInfinite.data?.pages), [searchInfinite.data]);

  const refetchSearch = async () => {
    const result = await searchInfinite.refetch();
    return { data: mergeSearchPages(result.data?.pages) };
  };

  return {
    fetchSuggest: suggestMutation.mutateAsync,
    suggestData: suggestMutation.data,
    suggestLoading: suggestMutation.isPending,
    fetchPredictive: predictiveMutation.mutateAsync,
    predictiveData: predictiveMutation.data,
    predictiveLoading: predictiveMutation.isPending,
    fetchSearch: (variables: Record<string, unknown>) => {
      setSearchVariables(variables);
    },
    searchData,
    searchLoading: searchInfinite.isFetching,
    fetchMoreSearch: () => searchInfinite.fetchNextPage(),
    refetchSearch,
  };
}
